import React, { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { FormInput } from "go-form";
import useFlash from "go-alert/AlertMessage";
import FileUpload from "go-app/components/ImageForm/FileUpload";
import handleError from "go-app/services/errors";
import { ReactComponent as EmptyImageSVG } from "go-component/images/svg/angle-input.svg";
import { FormDirty } from "go-form/components/FormDirty";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { useCustomErrors, useCustomValidation } from "go-form/hooks";
import { CustomValidationConfig, CustomValidationConstraint } from "go-form/services/types";
import { adminApi } from "../../../../../../../services/Api/Admin/adminApi";
import { AppVersionApi } from "../../../../../../../services/Api/Admin/adminTypes";
import FileDisplay from "../../../../../components/FileUpload/FileDisplay";

interface Props {
	appVersion?: AppVersionApi;
	isEditForm: boolean;
}

const AppVersionForm = ({ appVersion, isEditForm }: Props) => {
	const form = useForm<AppVersionApi>({
		criteriaMode: "all",
		defaultValues: appVersion,
	});
	const {
		register,
		handleSubmit,
		formState: { errors },
		setError,
		formState,
		control,
		watch,
	} = form;
	const [loading, setLoading] = useState(false);
	const [selectedFile, setSelectedFile] = useState<File | undefined>(undefined);
	const { t } = useTranslation("admin");
	const history = useHistory();
	const { addFlash, addSuccessFlash } = useFlash();
	const { setErrors, validateCustomErrors } = useCustomErrors(setError);

	const getAppTypes = () => {
		return [
			{ label: "POS", value: "POS" },
			{ label: "SERVER", value: "SERVER" },
			{ label: "GASTRO_CONTROL", value: "GASTRO_CONTROL" },
			{ label: "UPDATE", value: "UPDATE" },
			{ label: "SELF_UPDATE", value: "SELF_UPDATE" },
			{ label: "WEIGHT_SCALE", value: "WEIGHT_SCALE" },
			{ label: "MIGRATIONS", value: "MIGRATIONS" },
			{ label: "CUSTOMER_DISPLAY", value: "CUSTOMER_DISPLAY" },
			{ label: "HOTEL_INTEGRATION", value: "HOTEL_INTEGRATION" },
			{ label: "DELIVERY", value: "DELIVERY" },
			{ label: "ANDROID_POS", value: "ANDROID_POS" },
			{ label: "ANDROID_KDS", value: "ANDROID_KDS" },
			{ label: "ANDROID_INVENTORY", value: "ANDROID_INVENTORY" },
			{ label: "ANDROID_MANAGER", value: "ANDROID_MANAGER" },
			{ label: "IOS_MANAGER", value: "IOS_MANAGER" },
			{ label: "ANDROID_KIOSK", value: "ANDROID_KIOSK" },
			{ label: "ANDROID_LAUNCHER", value: "ANDROID_LAUNCHER" },
			{ label: "ANDROID_TEAM_VIEWER", value: "ANDROID_TEAM_VIEWER" },
			{ label: "ANDROID_PHONE_CENTRAL", value: "ANDROID_PHONE_CENTRAL" },
			{ label: "ANDROID_TEAM_VIEWER_HOST", value: "ANDROID_TEAM_VIEWER_HOST" },
			{ label: "ANDROID_INVENTORY", value: "ANDROID_INVENTORY" },
			{ label: "ANDROID_AIR_DROID", value: "ANDROID_AIR_DROID" },
			{ label: "ANDROID_CUSTOMER_DISPLAY", value: "ANDROID_CUSTOMER_DISPLAY" },
			{ label: "ANDROID_ORDER", value: "ANDROID_ORDER" },
			{ label: "ANDROID_ANYDESK", value: "ANDROID_ANYDESK" },
			{ label: "ANDROID_TEAM_VIEWER_QUICK_SUPPORT", value: "ANDROID_TEAM_VIEWER_QUICK_SUPPORT" },
			{ label: "ANDROID_ANY_DESK_ADDON", value: "ANDROID_ANY_DESK_ADDON" },
			{ label: "ANDROID_TEAM_VIEWER_UNIVERSAL_ADDON", value: "ANDROID_TEAM_VIEWER_UNIVERSAL_ADDON" },
			{ label: "ANDROID_POS_V2", value: "ANDROID_POS_V2" },
			{ label: "ANDROID_POS_V2_MAPPING", value: "ANDROID_POS_V2_MAPPING" },
			{ label: "ANDROID_ESERVICE_GPTOM", value: "ANDROID_ESERVICE_GPTOM" },
			{ label: "ANDROID_XPAY_XKASA", value: "ANDROID_XPAY_XKASA" },
		];
	};
	const getAppStatuses = () => {
		return [
			{ label: "DRAFT", value: "DRAFT" },
			{ label: "TEST", value: "TEST" },
			{ label: "PROD", value: "PROD" },
		];
	};

	const onUpdateFile = (fileObj?: File) => {
		setSelectedFile(fileObj);
	};

	const onSubmit = handleSubmit(async (data: AppVersionApi) => {
		if (!validateCustomErrors()) {
			return;
		}

		!isEditForm ? await onCreate(data) : await onUpdate(data);
	});

	const onCreate = async (data: AppVersionApi) => {
		try {
			setLoading(true);
			const formData = new FormData();
			selectedFile && formData.append("file", selectedFile);

			const dto = {
				...data,
				name: data.name || null,
				app_type: data.app_type || null,
				description: data.description || null,
				status: data.status || null,
				change_log: data.change_log || null,
				version: data.version || null,
			};

			formData.append("dto", new Blob([JSON.stringify(dto)], { type: "application/json" }));
			await adminApi.createAppVersion(formData);
			addSuccessFlash(t("common.flash.saved", { ns: "lib" }));
			history.push(`/admin/app_versions`);
		} catch (e) {
			handleError.form(e, setError, addFlash);
		}
		setLoading(false);
	};

	const onUpdate = async (data: AppVersionApi) => {
		if (appVersion?.id) {
			try {
				setLoading(true);
				await adminApi.updateAppVersion(appVersion.id, data);
				addSuccessFlash(t("common.flash.saved", { ns: "lib" }));
			} catch (e) {
				handleError.form(e, setError, addFlash);
			}
			setLoading(false);
		}
	};

	const nameValidationConfig = useMemo(
		() => ({
			types: [{ constraint: CustomValidationConstraint.REQUIRED }],
			setErrors,
			utils: { watch },
		}),
		[setErrors, watch]
	) satisfies CustomValidationConfig;

	const versionValidationConfig = useMemo(
		() => ({
			types: [{ constraint: CustomValidationConstraint.REQUIRED }],
			setErrors,
			utils: { watch },
		}),
		[setErrors, watch]
	) satisfies CustomValidationConfig;

	const typeValidationConfig = useMemo(
		() => ({
			types: [{ constraint: CustomValidationConstraint.REQUIRED }],
			setErrors,
		}),
		[setErrors]
	) satisfies CustomValidationConfig;

	const statusValidationConfig = useMemo(
		() => ({
			types: [{ constraint: CustomValidationConstraint.REQUIRED }],
			setErrors,
		}),
		[setErrors]
	) satisfies CustomValidationConfig;

	const fileValidationConfig = useMemo(() => {
		return {
			types: [{ constraint: CustomValidationConstraint.REQUIRED_IF, isRequired: () => !isEditForm }],
			setErrors,
			utils: { watch },
		};
	}, [setErrors, isEditForm, watch]) satisfies CustomValidationConfig;

	useCustomValidation({ name: "file", value: selectedFile?.name, customValidationConfig: fileValidationConfig });

	return (
		<FormDirty formState={formState} key="app-version-form" loading={loading} noValidate onSubmit={onSubmit}>
			<fieldset className={"form-group no-gutters"}>
				<h5>{t("common.word.basic_data", { ns: "lib" })}</h5>
				<div className="row">
					<div className={"col-md-8"}>
						<FormInput
							label={t("lib:common.word.name")}
							register={register}
							name="name"
							errors={errors}
							customValidationConfig={nameValidationConfig}
						/>
						<FormInput
							label={t("modules.app_version.field.version.title")}
							register={register}
							name="version"
							type="number"
							errors={errors}
							customValidationConfig={versionValidationConfig}
						/>
						<FormSelectGroup
							label={t("modules.app_version.field.app_type.title")}
							name="app_type"
							placeholder={t("modules.app_version.field.app_type.placeholder")}
							errors={errors}
							getOptionLabel={(option) => t(`enums.app_versions.type.${option.label}`)}
							defaultValue={{ label: appVersion?.app_type, value: appVersion?.app_type }}
							loadOptions={getAppTypes}
							control={control}
							customValidationConfig={typeValidationConfig}
							data-testid="app_type"
						/>
						<FormInput
							as={"textarea"}
							rows={5}
							label={t("lib:common.word.description")}
							register={register}
							name="description"
							errors={errors}
						/>
						<FormSelectGroup
							label={t("modules.app_version.field.status.title")}
							name="status"
							placeholder={t("modules.app_version.field.status.placeholder")}
							errors={errors}
							getOptionLabel={(option) => t(`enums.app_versions.status.${option.label}`)}
							defaultValue={{ label: appVersion?.status, value: appVersion?.status }}
							loadOptions={getAppStatuses}
							control={control}
							customValidationConfig={statusValidationConfig}
							data-testid="status"
						/>
						<FormInput
							as={"textarea"}
							rows={5}
							label={t("modules.app_version.field.changes.title")}
							register={register}
							name="change_log"
							errors={errors}
						/>
						{!isEditForm && (
							<div className="mt-2">
								<FormInput
									label={t("modules.app_version.field.file.title")}
									name="file"
									errors={errors}
									register={register}
									customValidationConfig={fileValidationConfig}
									hidden
								/>
								<FileUpload
									fileIcon={
										selectedFile ? (
											<FileDisplay icon={<EmptyImageSVG />} fileName={selectedFile.name} />
										) : undefined
									}
									onUpdateFile={(_, fileObj) => onUpdateFile(fileObj)}
								/>
							</div>
						)}
					</div>
				</div>
			</fieldset>
		</FormDirty>
	);
};

export default AppVersionForm;
